import { BACKEND_URL } from "@/lib/api";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, CheckCircle2, Trophy, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStepTop } from "@/lib/useStepTop";
import { StepCheer, useStepCheer } from "@/components/portal/StepCheer";

const API = `${BACKEND_URL}/api`;

const CHANNELS = [
  { id: "newsletter", label: "Newsletter / blog" },
  { id: "linkedin",   label: "LinkedIn / X content" },
  { id: "podcast",    label: "Podcast / video" },
  { id: "agency",     label: "Agency or consultancy" },
  { id: "community",  label: "Community / membership" },
  { id: "other",      label: "Something else" },
];
const AUDIENCE_SIZES = [
  { id: "<5k",      label: "Under 5,000" },
  { id: "5k-25k",   label: "5,000 – 25,000" },
  { id: "25k-100k", label: "25,000 – 100,000" },
  { id: "100k+",    label: "Over 100,000" },
];
const SECTORS = [
  "Mortgages", "Life Insurance", "Pensions", "Debt Solutions",
  "Personal Loans", "Crypto", "Equity Release", "Other",
];

export default function PartnersApply() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const topRef = useStepTop(step);
  const cheer = useStepCheer(step, [null, "About you · done", "Your channel · done"]);

  const [form, setForm] = useState({
    name: "", email: "", company: "", country: "GB", website: "",
    channel: "newsletter", audience_size: "5k-25k", monthly_reach: "",
    sectors: [], motivation: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSector = (s) => setForm((f) => ({ ...f, sectors: f.sectors.includes(s) ? f.sectors.filter(x => x !== s) : [...f.sectors, s] }));

  const step1Valid = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email);
  const step2Valid = !!form.channel && !!form.audience_size;

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/partners/apply`, form);
      setDone(true);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit your application — try again in a moment.");
    } finally { setBusy(false); }
  };

  // ── Success state ──
  if (done) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-white">
        <section className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="inline-grid place-items-center size-14 rounded-full bg-[#0055FF]/10 text-[#0055FF] mb-6">
            <CheckCircle2 className="size-7" strokeWidth={1.8} />
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold text-slate-900 leading-tight">Application received.</h1>
          <p className="mt-5 text-[16px] text-slate-600 max-w-xl mx-auto leading-relaxed">
            Thanks {form.name.split(" ")[0]} — our partner team reviews every application personally. You'll hear back within one working day, usually faster.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/partners"><Button variant="outline" className="rounded-full h-11 px-6" data-testid="apply-success-back">Back to programme</Button></Link>
            <Link to="/leaderboard"><Button className="rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="apply-success-leaderboard">See top earners <ArrowUpRight className="size-4 ml-1" /></Button></Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="partners-apply">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_-10%,rgba(0,85,255,0.08),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_110%,rgba(34,211,166,0.08),transparent_55%)] pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 py-14 lg:py-16 relative">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold mb-3">
            <Trophy className="size-3.5" strokeWidth={1.8} /> Partner programme · application
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 leading-tight">Apply to Connexis Partners.</h1>
          <p className="mt-3 text-slate-600 max-w-xl text-[15px]">Tell us about you and your channel — we review every application personally and reply within 1 working day.</p>

          {/* Progress dots */}
          <div ref={topRef} className="mt-8 flex items-center gap-2.5 scroll-mt-24" data-testid="apply-progress">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2.5">
                <div className={`flex items-center justify-center size-7 rounded-full text-[11px] font-mono font-semibold transition-colors duration-300
                  ${s < step ? "bg-[#0055FF] text-white" : s === step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                  {s < step ? <CheckCircle2 className="size-3.5" /> : s}
                </div>
                {s < 3 && <div className={`h-px w-8 ${s < step ? "bg-[#0055FF]" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative max-w-3xl mx-auto px-6 py-12">
        <StepCheer cheer={cheer} at="34px" testId="apply-step-cheer" />
        <Card className="rounded-2xl border-slate-200 shadow-soft">
          <CardContent className="p-7 sm:p-9">

            {/* ───── STEP 1 ───── */}
            {step === 1 && (
              <div className="space-y-6" data-testid="apply-step-1">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold">Step 1 of 3</div>
                  <h2 className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900">About you</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Full name</Label>
                    <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Marcus Ali" className="mt-2 h-11" data-testid="apply-name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Work email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="marcus@growth.co" className="mt-2 h-11" data-testid="apply-email" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Company / brand</Label>
                    <Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" className="mt-2 h-11" data-testid="apply-company" />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Country (ISO-2)</Label>
                    <Input id="country" value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} placeholder="GB" className="mt-2 h-11 font-mono uppercase" data-testid="apply-country" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="website" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Website / portfolio</Label>
                    <Input id="website" type="url" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://… (optional)" className="mt-2 h-11" data-testid="apply-website" />
                  </div>
                </div>
              </div>
            )}

            {/* ───── STEP 2 ───── */}
            {step === 2 && (
              <div className="space-y-6" data-testid="apply-step-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold">Step 2 of 3</div>
                  <h2 className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900">Your channel</h2>
                </div>

                <div>
                  <Label className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Primary channel</Label>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CHANNELS.map((c) => {
                      const active = form.channel === c.id;
                      return (
                        <button key={c.id} type="button" onClick={() => set("channel", c.id)}
                          className={`px-3 py-2.5 rounded-lg border text-[13px] text-left transition-colors duration-150
                            ${active ? "border-[#0055FF] bg-[#0055FF]/5 text-[#0055FF] ring-1 ring-[#0055FF]/15" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                          data-testid={`apply-channel-${c.id}`}>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Audience size</Label>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {AUDIENCE_SIZES.map((a) => {
                      const active = form.audience_size === a.id;
                      return (
                        <button key={a.id} type="button" onClick={() => set("audience_size", a.id)}
                          className={`px-3 py-2.5 rounded-lg border text-[13px] transition-colors duration-150
                            ${active ? "border-[#0055FF] bg-[#0055FF]/5 text-[#0055FF] ring-1 ring-[#0055FF]/15" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                          data-testid={`apply-audience-${a.id}`}>
                          {a.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Sectors you reach (any)</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {SECTORS.map((s) => {
                      const active = form.sectors.includes(s);
                      return (
                        <button key={s} type="button" onClick={() => toggleSector(s)}
                          className={`px-3 py-1.5 rounded-full border text-[12.5px] transition-colors duration-150
                            ${active ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300 bg-white"}`}
                          data-testid={`apply-sector-${s.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="reach" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Monthly reach (optional)</Label>
                  <Input id="reach" value={form.monthly_reach} onChange={(e) => set("monthly_reach", e.target.value)} placeholder="~12,000 visits / 4,500 subscribers" className="mt-2 h-11" data-testid="apply-reach" />
                </div>
              </div>
            )}

            {/* ───── STEP 3 ───── */}
            {step === 3 && (
              <div className="space-y-6" data-testid="apply-step-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold">Step 3 of 3</div>
                  <h2 className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900">Why Connexis?</h2>
                </div>
                <div>
                  <Label htmlFor="motivation" className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-medium">Tell us briefly</Label>
                  <Textarea id="motivation" value={form.motivation} onChange={(e) => set("motivation", e.target.value)}
                    placeholder="Anything that helps us understand why you'd be a great partner — fit with your audience, similar partnerships, how you'd promote us…"
                    className="mt-2 min-h-[160px] resize-none" data-testid="apply-motivation" />
                  <div className="mt-2 text-[11px] text-slate-400 text-right font-mono tabular-nums">{form.motivation.length} / 800</div>
                </div>

                {/* Summary preview */}
                <div className="rounded-xl bg-slate-50/60 border border-slate-200 p-5 text-[13px] text-slate-700 space-y-1.5">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono font-semibold mb-2.5">Review</div>
                  <div><span className="text-slate-500">Name · </span>{form.name || "—"}</div>
                  <div><span className="text-slate-500">Email · </span>{form.email || "—"}</div>
                  <div><span className="text-slate-500">Channel · </span>{CHANNELS.find(c => c.id === form.channel)?.label}</div>
                  <div><span className="text-slate-500">Audience · </span>{AUDIENCE_SIZES.find(a => a.id === form.audience_size)?.label}</div>
                  <div><span className="text-slate-500">Sectors · </span>{form.sectors.join(", ") || "—"}</div>
                </div>
              </div>
            )}

            {/* ───── NAV ───── */}
            <div className="mt-10 flex items-center justify-between gap-3">
              <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} className="rounded-full" data-testid="apply-back">
                <ArrowLeft className="size-4" /> Back
              </Button>
              {step < 3 ? (
                <Button
                  disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
                  onClick={() => setStep(step + 1)}
                  className="rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20"
                  data-testid="apply-next"
                >
                  Continue <ArrowRight className="size-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={busy}
                  className="rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-md shadow-[#0055FF]/20"
                  data-testid="apply-submit">
                  {busy ? <><Loader2 className="size-4 animate-spin mr-1" /> Submitting…</> : <>Submit application <ArrowRight className="size-4 ml-1" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-[12px] text-slate-500">
          Prefer email? Reach us directly at <a href="mailto:kieran@connex.is" className="text-[#0055FF] hover:underline">kieran@connex.is</a>
        </div>
      </section>
    </div>
  );
}
